import { useData } from "@providers/context";
import useProjectId from "./useProjectId";

/**
 * Custom hook for checking project-specific permissions only
 * Does NOT include global/organization-level permissions
 */
export const useProjectPermission = () => {
  const { projectPermission } = useData();
  const projectId = useProjectId();

  /**
   * Check if user has a specific permission for the current project (project-specific only)
   * @param permit - Permission string to check
   * @returns boolean - true if user has the permission for this project
   */
  const projectPermissionCheck = (permit: string | undefined): boolean => {
    if (!permit || !projectId) return false;

    // Get project-specific permissions only
    const projectPerms = projectPermission?.[projectId] || [];
    return projectPerms.includes(permit);
  };

  /**
   * Check if user has any of the specified permissions for the current project (project-specific only)
   * @param permits - Array of permission strings to check
   * @returns boolean - true if user has at least one of the permissions for this project
   */
  const multipleProjectPermissionCheck = (permits: any): boolean => {
    if (!permits || !projectId) return false;

    const check = permits?.map((val: string) => projectPermissionCheck(val));
    let result = false;

    for (let i = 0; i < check?.length; i++) {
      result = result || check[i];
      if (result === true) {
        break; // Short-circuit if result is already true
      }
    }

    return result;
  };

  return {
    projectPermissionCheck,
    multipleProjectPermissionCheck,
  };
};

//   Implementation example:
//   const { projectPermissionCheck, multipleProjectPermissionCheck } = useProjectPermission();
